import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import Langs from "../../app/lang/langs";

/**
 * Report & prime configuration for one evaluation type.
 *
 * Presentational only — the parent owns the state and the save. Everything shown here
 * maps to a column in `evaluations_types_config` or a row in `evaluations_types_tiers`,
 * which is what lets a type created in Settings produce a prime report with no code
 * change.
 *
 * The quadrimestre layout (Q1 Jan–Apr, Q2 May–Aug, Q3 Sep–Dec) is deliberately fixed and
 * shown as a note rather than a field — see evaluations_configurable_reports.md §2.
 */

export const DEFAULT_REPORT_CONFIG = {
    member_source: "TEAM_MEMBERS",
    manager_profile_mode: "ANY",
    id_manager_profile: "",
    exclude_team_managers: true,
    eligibility_source: "MEMBERSHIP_DATES",
    leaves_threshold: "",
    leaves_comparator: "GT",
    absences_threshold: "",
    team_impact_enabled: false,
};

export default function ReportConfigSection({
    config,
    tiers,
    managersProfiles = [],
    onConfigChange,
    onTiersChange,
    disabled = false,
}) {

    const fromManagers = config.member_source === "TEAM_MANAGERS";
    const needsProfile = fromManagers && config.manager_profile_mode !== "ANY";

    // A step with no amount is meaningless, and two steps for the same count make the
    // saved schedule ambiguous — surface both before the user hits save.
    const duplicateCounts = tiers.reduce((acc, tier, index) => {
        const first = tiers.findIndex(t => String(t.missed_count) === String(tier.missed_count));
        if (first !== index) acc.add(index);
        return acc;
    }, new Set());

    const set = (key) => (value) => onConfigChange({ ...config, [key]: value });

    const updateTier = (index, key, value) => {
        const next = tiers.map((tier, i) => (i === index ? { ...tier, [key]: value } : tier));
        onTiersChange(next);
    };

    const addTier = () => {
        // Default the new step to the next unused count so the common case is one click.
        const used = tiers.map(t => parseInt(t.missed_count)).filter(n => !isNaN(n));
        const nextCount = used.length ? Math.max(...used) + 1 : 0;
        onTiersChange([...tiers, { missed_count: String(nextCount), amount: "" }]);
    };

    const removeTier = (index) => onTiersChange(tiers.filter((_, i) => i !== index));

    return (
        <Accordion disableGutters defaultExpanded={false} sx={{ mt: 2, boxShadow: "none", border: "1px solid #e0e0e0" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                    <Langs str="EVAL_REPORT_CONFIG_TITLE" />
                </Typography>
            </AccordionSummary>

            <AccordionDetails>
                <Stack spacing={2.5}>

                    <Alert severity="info" sx={{ py: 0.5 }}>
                        <Langs str="EVAL_CFG_PERIODS_NOTE" />
                    </Alert>

                    {/* ── Who the report covers ───────────────────────────────── */}
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <FormControl fullWidth size="small" disabled={disabled}>
                            <InputLabel><Langs str="EVAL_CFG_MEMBER_SOURCE" /></InputLabel>
                            <Select
                                label={<Langs str="EVAL_CFG_MEMBER_SOURCE" />}
                                value={config.member_source}
                                onChange={(e) => {
                                    const member_source = e.target.value;
                                    // Leaving TEAM_MANAGERS makes the profile filter
                                    // meaningless — reset it so a stale ONLY/EXCEPT
                                    // cannot be saved against team_members.
                                    onConfigChange({
                                        ...config,
                                        member_source,
                                        manager_profile_mode: member_source === "TEAM_MANAGERS"
                                            ? config.manager_profile_mode
                                            : "ANY",
                                        id_manager_profile: member_source === "TEAM_MANAGERS"
                                            ? config.id_manager_profile
                                            : "",
                                    });
                                }}
                            >
                                <MenuItem value="TEAM_MEMBERS"><Langs str="EVAL_CFG_SRC_TEAM_MEMBERS" /></MenuItem>
                                <MenuItem value="TEAM_MANAGERS"><Langs str="EVAL_CFG_SRC_TEAM_MANAGERS" /></MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" disabled={disabled}>
                            <InputLabel><Langs str="EVAL_CFG_ELIGIBILITY" /></InputLabel>
                            <Select
                                label={<Langs str="EVAL_CFG_ELIGIBILITY" />}
                                value={config.eligibility_source}
                                onChange={(e) => set("eligibility_source")(e.target.value)}
                            >
                                <MenuItem value="MEMBERSHIP_DATES"><Langs str="EVAL_CFG_ELIG_MEMBERSHIP" /></MenuItem>
                                <MenuItem value="CONTRACT_DATES"><Langs str="EVAL_CFG_ELIG_CONTRACT" /></MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    {fromManagers && (
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                            <FormControl fullWidth size="small" disabled={disabled}>
                                <InputLabel><Langs str="EVAL_CFG_PROFILE_MODE" /></InputLabel>
                                <Select
                                    label={<Langs str="EVAL_CFG_PROFILE_MODE" />}
                                    value={config.manager_profile_mode}
                                    onChange={(e) => set("manager_profile_mode")(e.target.value)}
                                >
                                    <MenuItem value="ANY"><Langs str="EVAL_CFG_MODE_ANY" /></MenuItem>
                                    <MenuItem value="ONLY"><Langs str="EVAL_CFG_MODE_ONLY" /></MenuItem>
                                    <MenuItem value="EXCEPT"><Langs str="EVAL_CFG_MODE_EXCEPT" /></MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl
                                fullWidth
                                size="small"
                                disabled={disabled || !needsProfile}
                                error={needsProfile && !config.id_manager_profile}
                            >
                                <InputLabel><Langs str="EVAL_CFG_PROFILE" /></InputLabel>
                                <Select
                                    label={<Langs str="EVAL_CFG_PROFILE" />}
                                    value={config.id_manager_profile ?? ""}
                                    onChange={(e) => set("id_manager_profile")(e.target.value)}
                                >
                                    {managersProfiles.map(profile => (
                                        <MenuItem key={profile.id} value={profile.id}>
                                            {profile.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    )}

                    {!fromManagers && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!config.exclude_team_managers}
                                    onChange={(e) => set("exclude_team_managers")(e.target.checked)}
                                    disabled={disabled}
                                />
                            }
                            label={<Langs str="EVAL_CFG_EXCLUDE_MANAGERS" />}
                        />
                    )}

                    <Divider />

                    {/* ── Prime schedule ──────────────────────────────────────── */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            <Langs str="EVAL_CFG_TIERS_TITLE" />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            <Langs str="EVAL_CFG_TIERS_HINT" />
                        </Typography>

                        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                            {tiers.map((tier, index) => (
                                <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={<Langs str="EVAL_CFG_TIER_MISSED" />}
                                        value={tier.missed_count}
                                        onChange={(e) => updateTier(index, "missed_count", e.target.value)}
                                        inputProps={{ min: 0 }}
                                        disabled={disabled}
                                        error={duplicateCounts.has(index)}
                                        helperText={duplicateCounts.has(index) ? <Langs str="EVAL_CFG_TIER_DUPLICATE" /> : " "}
                                        sx={{ width: 160 }}
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={<Langs str="EVAL_CFG_TIER_AMOUNT" />}
                                        value={tier.amount}
                                        onChange={(e) => updateTier(index, "amount", e.target.value)}
                                        inputProps={{ min: 0, step: "0.01" }}
                                        disabled={disabled}
                                        error={tier.amount === "" || tier.amount === null}
                                        helperText=" "
                                        sx={{ width: 160 }}
                                    />
                                    <Tooltip title={<Langs str="DELETE" />}>
                                        <IconButton
                                            onClick={() => removeTier(index)}
                                            disabled={disabled}
                                            size="small"
                                            sx={{ mt: 0.5 }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            ))}

                            <Box>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addTier}
                                    disabled={disabled}
                                    size="small"
                                >
                                    <Langs str="EVAL_CFG_ADD_TIER" />
                                </Button>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* ── Penalties ───────────────────────────────────────────── */}
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={<Langs str="EVAL_CFG_LEAVES_THRESHOLD" />}
                            value={config.leaves_threshold}
                            onChange={(e) => set("leaves_threshold")(e.target.value)}
                            inputProps={{ min: 0, step: "0.5" }}
                            disabled={disabled}
                            helperText={<Langs str="EVAL_CFG_NO_RULE" />}
                        />

                        <FormControl
                            fullWidth
                            size="small"
                            disabled={disabled || config.leaves_threshold === ""}
                        >
                            <InputLabel><Langs str="EVAL_CFG_COMPARATOR" /></InputLabel>
                            <Select
                                label={<Langs str="EVAL_CFG_COMPARATOR" />}
                                value={config.leaves_comparator}
                                onChange={(e) => set("leaves_comparator")(e.target.value)}
                            >
                                <MenuItem value="GT"><Langs str="EVAL_CFG_CMP_GT" /></MenuItem>
                                <MenuItem value="GTE"><Langs str="EVAL_CFG_CMP_GTE" /></MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={<Langs str="EVAL_CFG_ABSENCES_THRESHOLD" />}
                            value={config.absences_threshold}
                            onChange={(e) => set("absences_threshold")(e.target.value)}
                            inputProps={{ min: 0 }}
                            disabled={disabled}
                            helperText={<Langs str="EVAL_CFG_NO_RULE" />}
                        />
                    </Stack>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!config.team_impact_enabled}
                                onChange={(e) => set("team_impact_enabled")(e.target.checked)}
                                disabled={disabled}
                            />
                        }
                        label={<Langs str="EVAL_CFG_TEAM_IMPACT" />}
                    />

                    <Alert severity="warning" sx={{ py: 0.5 }}>
                        <Langs str="EVAL_CFG_FIELD_DEACTIVATE_WARNING" />
                    </Alert>

                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}

/**
 * Turns the form state into the request body, and reports what is wrong with it.
 *
 * Kept next to the form so the client-side rules and the server-side rules in
 * `saveEvaluationTypeConfig` stay recognisably the same shape.
 *
 * @returns {{valid: boolean, errors: Array<string>, body: object}}
 */
export function buildReportConfigPayload(idType, config, tiers) {
    const errors = [];

    const needsProfile = config.member_source === "TEAM_MANAGERS"
        && config.manager_profile_mode !== "ANY";

    if (needsProfile && !config.id_manager_profile) {
        errors.push("EVAL_CFG_ERR_PROFILE_REQUIRED");
    }

    const seen = new Set();
    for (const tier of tiers) {
        const missed = parseInt(tier.missed_count);
        const amount = parseFloat(tier.amount);
        if (isNaN(missed) || missed < 0 || isNaN(amount) || amount < 0) {
            errors.push("EVAL_CFG_ERR_TIER_INVALID");
            break;
        }
        if (seen.has(missed)) {
            errors.push("EVAL_CFG_ERR_TIER_DUPLICATE");
            break;
        }
        seen.add(missed);
    }

    return {
        valid: errors.length === 0,
        errors,
        body: {
            idType,
            member_source: config.member_source,
            manager_profile_mode: config.manager_profile_mode,
            id_manager_profile: needsProfile ? config.id_manager_profile : null,
            exclude_team_managers: !!config.exclude_team_managers,
            eligibility_source: config.eligibility_source,
            // Empty means "no rule", which is not the same as a threshold of 0.
            leaves_threshold: config.leaves_threshold === "" ? null : parseFloat(config.leaves_threshold),
            leaves_comparator: config.leaves_comparator,
            absences_threshold: config.absences_threshold === "" ? null : parseInt(config.absences_threshold),
            team_impact_enabled: !!config.team_impact_enabled,
            tiers: tiers.map(t => ({
                missed_count: parseInt(t.missed_count),
                amount: parseFloat(t.amount),
            })),
        },
    };
}
